import React, { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Domain, LocationOn } from '@mui/icons-material';
import { Avatar, Stack, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';

import { ContactRow } from './ContactRow';
import { getContactId } from '../../../lib';
import { useDealBuilderContext } from '../../../context';
import { ContactSearchAutocomplete } from './ContactSearchAutocomplete';
import { DealBuilderContact, DealBuilderFormValues, DealBuilderIds, StepComponentProps } from '../../../types';
import {
  dealBuilderApi,
  StartDealBuilderContactInput,
  UpsertDealBuilderContactsResponse,
  UpsertDealBuilderContactsVariables,
} from '../../../api';

export const ContactsStep = ({ navigationNode }: StepComponentProps) => {
  const {
    contacts: { contactsData },
  } = useDealBuilderContext();

  const { account, primaryContact, additionalRecipients, allContacts } = contactsData;

  const [upsertContactsMutation, { loading, error }] = useMutation<
    UpsertDealBuilderContactsResponse,
    UpsertDealBuilderContactsVariables
  >(dealBuilderApi.upsertDealBuilderContacts);

  const { control, setValue } = useFormContext<DealBuilderFormValues>();

  const ownerPgIds = useWatch({ control, name: 'contacts.ownerPgIds' });
  const accountContactIds = useWatch({ control, name: 'contacts.accountContactIds' });

  const selectedIds = useMemo(() => new Set([...ownerPgIds, ...accountContactIds]), [ownerPgIds, accountContactIds]);

  const saveContactsStep = async ({ proposalId }: DealBuilderIds) => {
    const contacts: StartDealBuilderContactInput[] = [
      ...ownerPgIds.map(id => ({ ownerPgId: id })),
      ...accountContactIds.map(id => ({ accountContactId: id })),
    ];

    await upsertContactsMutation({ variables: { input: { proposalId, contacts } } });
  };

  // Tracks insertion order for contacts added via autocomplete
  const [addedOrder, setAddedOrder] = useState<string[]>([]);

  const visibleRecipients = useMemo(() => {
    const shownIds = new Set([
      ...additionalRecipients.map(c => getContactId(c)),
      primaryContact ? getContactId(primaryContact) : '',
    ]);

    const contactById = new Map(allContacts.map(c => [getContactId(c), c]));
    const extra: DealBuilderContact[] = [];
    // Show previously-saved contacts that aren't in the default visible set
    [...ownerPgIds, ...accountContactIds].forEach(id => {
      if (!shownIds.has(id) && contactById.has(id)) {
        shownIds.add(id);
        extra.push(contactById.get(id)!);
      }
    });

    for (const id of addedOrder) {
      if (!shownIds.has(id) && contactById.has(id)) {
        shownIds.add(id);
        extra.push(contactById.get(id)!);
      }
    }

    return [...additionalRecipients, ...extra];
  }, [additionalRecipients, allContacts, ownerPgIds, accountContactIds, addedOrder, primaryContact]);

  const searchableContacts = useMemo(() => {
    const visibleIds = new Set([
      primaryContact ? getContactId(primaryContact) : undefined,
      ...visibleRecipients.map(c => getContactId(c)),
    ]);

    return allContacts.filter(c => !visibleIds.has(getContactId(c)));
  }, [allContacts, primaryContact, visibleRecipients]);

  const toggleContact = async (contact: DealBuilderContact) => {
    const id = getContactId(contact);

    if (contact.ownerPgId) {
      const next = ownerPgIds.includes(id) ? ownerPgIds.filter(v => v !== id) : [...ownerPgIds, id];
      setValue('contacts.ownerPgIds', next);
    } else {
      const next = accountContactIds.includes(id)
        ? accountContactIds.filter(v => v !== id)
        : [...accountContactIds, id];
      setValue('contacts.accountContactIds', next);
    }
  };

  const handleSearchSelect = (contact: DealBuilderContact) => {
    const id = getContactId(contact);

    setAddedOrder(prev => [...prev, id]);

    if (contact.ownerPgId) {
      const next = [...ownerPgIds, id];
      setValue('contacts.ownerPgIds', next);
    } else {
      const next = [...accountContactIds, id];
      setValue('contacts.accountContactIds', next);
    }
  };

  return (
    <CardContainer title="Contacts" subTitle="Select the contacts who will receive this quote">
      <Stack gap={0.5}>
        <Typography variant="subtitle2" component="span">
          Account
        </Typography>

        <Stack
          gap={1.5}
          direction="row"
          alignItems="center"
          sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
        >
          <Avatar>
            <Domain />
          </Avatar>

          <Stack>
            <Typography variant="subtitle2" component="span">
              {account?.name ?? 'No account'}
            </Typography>

            {account?.address && (
              <Stack direction="row" alignItems="flex-start" gap={0.5} sx={{ mt: 0.5 }}>
                <LocationOn fontSize="inherit" sx={{ fontSize: '16px', color: 'grey.500' }} />
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  Address:
                </Typography>

                <Typography variant="body2">{account.address}</Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>

      {primaryContact && (
        <Stack gap={0.5}>
          <Typography variant="subtitle2" component="span">
            Primary Contact
          </Typography>

          <ContactRow
            isPrimary
            avatarIndex={0}
            contact={primaryContact}
            onToggle={() => toggleContact(primaryContact)}
            checked={selectedIds.has(getContactId(primaryContact))}
          />
        </Stack>
      )}

      <Stack gap={0.5}>
        <Typography variant="subtitle2" component="span">
          Additional Recipients
        </Typography>

        <Stack gap={1}>
          {visibleRecipients.map((contact, index) => (
            <ContactRow
              contact={contact}
              avatarIndex={index + 1}
              key={getContactId(contact)}
              onToggle={() => toggleContact(contact)}
              checked={selectedIds.has(getContactId(contact))}
            />
          ))}
        </Stack>
      </Stack>

      <Stack direction="row" gap={2} alignItems="center">
        <ContactSearchAutocomplete
          options={searchableContacts}
          onSelect={handleSearchSelect}
          accountName={account?.name ?? null}
        />
      </Stack>

      {navigationNode({
        error,
        isLoading: loading,
        fn: saveContactsStep,
        isDisabled: !selectedIds.size,
        info: !selectedIds.size ? (
          <Typography>Please ensure you have selected at least one contact to proceed</Typography>
        ) : (
          ''
        ),
      })}
    </CardContainer>
  );
};
